import React from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import {
  Calendar,
  Globe,
  Heart,
  Link as LinkGlyph,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  ShoppingBag,
  Star,
  UserRound,
  Utensils,
  Wifi,
  type LucideProps,
} from 'lucide-react'

import { SOCIAL_BRANDS } from '@/constants/socialBrands'
import type { QrLogoIconStyle } from '@/lib/qr/design'

/**
 * Icons a QR code can carry in its centre. Only the key is saved with the design; the mark is
 * drawn at render time, so a card's QR never depends on a stored image for an icon logo.
 *
 * Two families: generic glyphs (what the code does — call, mail, visit, menu) inked in the code's
 * colour, and every social network in the brand catalogue, inked in its own brand colour.
 */

export type QrLogoIcon = {
  key: string
  name: string
  group: 'glyph' | 'social'
  /** Brand colour; `null` for glyphs, which follow the code's ink. */
  color: string | null
  onColor: string
  /** SVG markup at 24×24 for a given ink. */
  svg: (ink: string) => string
  /** Glyphs only: the component, for pickers that render the mark live. */
  Component?: React.ComponentType<LucideProps>
}

const glyph = (key: string, name: string, Icon: React.ComponentType<LucideProps>): QrLogoIcon => ({
  key,
  name,
  group: 'glyph',
  Component: Icon,
  color: null,
  onColor: '#FFFFFF',
  svg: (ink) =>
    renderToStaticMarkup(<Icon size={24} color={ink} strokeWidth={2.25} absoluteStrokeWidth />),
})

export const QR_LOGO_ICONS: QrLogoIcon[] = [
  glyph('glyph:phone', 'Phone', Phone),
  glyph('glyph:mail', 'Mail', Mail),
  glyph('glyph:globe', 'Website', Globe),
  glyph('glyph:link', 'Link', LinkGlyph),
  glyph('glyph:user', 'Contact', UserRound),
  glyph('glyph:map-pin', 'Location', MapPin),
  glyph('glyph:message', 'Message', MessageCircle),
  glyph('glyph:calendar', 'Booking', Calendar),
  glyph('glyph:wifi', 'Wi-Fi', Wifi),
  glyph('glyph:menu', 'Menu', Utensils),
  glyph('glyph:shop', 'Shop', ShoppingBag),
  glyph('glyph:star', 'Review', Star),
  glyph('glyph:heart', 'Favourite', Heart),
  ...SOCIAL_BRANDS.map<QrLogoIcon>((brand) => ({
    key: brand.type,
    name: brand.name,
    group: 'social',
    color: brand.color,
    onColor: brand.onColor ?? '#FFFFFF',
    svg: (ink) =>
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path fill="${ink}" d="${brand.path}"/></svg>`,
  })),
]

const ICONS_BY_KEY = new Map(QR_LOGO_ICONS.map((icon) => [icon.key, icon]))

export const getQrLogoIcon = (key?: string | null) => (key ? ICONS_BY_KEY.get(key) : undefined)

/** Ink and tile for an icon in a given style. `tile` overrides the design's logo background. */
export const qrIconColors = (icon: QrLogoIcon, style: QrLogoIconStyle, codeInk: string) => {
  const brand = icon.color ?? codeInk
  if (style === 'filled') return { ink: icon.color ? icon.onColor : '#FFFFFF', tile: brand }
  if (style === 'mono') return { ink: codeInk, tile: null }
  return { ink: brand, tile: null }
}

/** SVG markup as a data URL the compositor can draw. */
export const svgDataUrl = (svg: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
